import { useForm, yupResolver } from "@mantine/form";
import { useEffect, memo, useCallback, useState } from "react";
import {
  CreateClientInput,
  Gender,
  GetClientGuestLeadQuery,
  MemberStatus,
  useCreateClientMutation,
  useUpdateClientMutation,
} from "src/generated/graphql";
import { useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import { useDisclosure } from "@mantine/hooks";
import FormCard from "src/components/FormCard";
import {
  CardRadius,
  EMPLOYEE_ROLE_OBJ,
  HeightCard,
  MESSAGES,
  ModalId,
} from "src/utils/constants";
import { Box, Button, Flex, Group, useMantineTheme } from "@mantine/core";
import { closeModal, useModals } from "@mantine/modals";
import { object, string } from "yup";
import DatePicker from "src/components/DatePicker";
import useWritePermission from "src/hooks/usePermission";
import { useAppContext } from "src/api/AppContext";
import AutoLoginGeneration from "./ClientDetail/AutoLoginGeneration";
import { isAdult } from "src/utils/dateHelper";
import ActionCard from "src/components/ActionCard";
import Select from "src/components/Select";

type IFormProps = Omit<CreateClientInput, "dob"> & {
  dob: Date | undefined;
};

export const guestInitialValues = {
  firstName: "",
  lastName: "",
  email: "",
  dob: undefined as Date | undefined,

  // New fields
  homePhone: "",
  workPhone: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  idNumber: "",
  employer: "",
  gender: undefined,
  emergencyName: "",
  emergencyPhone: "",
  relationship: "",
};

export const guestFormSchema = object().shape({
  firstName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "First Name should must be least 2 letters")
    .max(30, "First Name must not be greater than 30 letters")
    .required("First Name is required"),
  lastName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Last Name should must be least 2 letters")
    .max(30, "Last Name must not be greater than 30 letters")
    .required("Last Name is required"),
  email: string().email().required("Email is required").max(50),

  // New fields
  homePhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  workPhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  address: string().max(200).notRequired(),
  city: string().max(50).notRequired(),
  state: string().max(50).notRequired(),
  zipCode: string().max(50).notRequired(),
  country: string().max(50).notRequired(),
  idNumber: string().max(30).notRequired(),
  employer: string().max(30).notRequired(),
  emergencyName: string().max(60).notRequired(),
  emergencyPhone: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  relationship: string().max(20).notRequired(),
});

interface GuestProfileProps {
  fetchGuest?: () => void;
  data?: GetClientGuestLeadQuery;
  refetchData?: () => void;
  onSuccess?: (attendeeId: string) => void;
  memberStatus?: MemberStatus;
}

const GuestLeadProfile = ({
  data,
  fetchGuest,
  refetchData,
  onSuccess,
  memberStatus = MemberStatus.Guest,
}: GuestProfileProps) => {
  const { currentLocation } = useAppContext();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.clients);
  const modals = useModals();
  const theme = useMantineTheme();
  const { clientId } = useParams<{ clientId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(
    !!clientId
  );
  const [isOldEnough, setisOldEnough] = useState(false);

  const [createClient, { loading: createLoading }] = useCreateClientMutation({
    onCompleted: (data) => {
      if (onSuccess) {
        onSuccess(data.createClient.attendee?.id || "");
      }
      closeModal(ModalId.CreateGuest);
      refetchData && refetchData();
    },
  });

  const [updateClient, { loading: updateLoading }] = useUpdateClientMutation({
    onCompleted: () => {
      toggleFormActive();
      form.resetDirty();
      fetchGuest && fetchGuest();
    },
  });

  useEffect(() => {
    resetFormState(data!);
  }, [data]);

  const form = useForm<IFormProps>({
    initialValues: guestInitialValues,
    validate: yupResolver(guestFormSchema),
  });

  const handleSubmit = async (values: IFormProps) => {
    const _obj = {
      ...values,
      memberStatus,
      dob: values.dob ? values.dob.toLocaleDateString() : null,
      // pass current location as default and as locations on create
      ...(!clientId &&
        currentLocation && {
          defaultLocation: { id: currentLocation.id },
          locations: [{ id: currentLocation.id }],
        }),
    };

    clientId
      ? await updateClient({
          variables: {
            data: _obj,
            id: clientId,
          },
        })
      : createClient({ variables: { data: _obj } });
  };

  const resetFormState = (guestData: GetClientGuestLeadQuery) => {
    if (!guestData?.clientOne) return;
    const {
      user,
      dob,
      homePhone,
      workPhone,
      address,
      city,
      state,
      country,
      zipCode,
      idNumber,
      employer,
      gender,
      emergencyName,
      emergencyPhone,
      relationship,
    } = guestData?.clientOne;
    const { firstName, lastName, email } = user || {};
    let _data = {
      firstName,
      lastName,
      email,
      dob: dob ? new Date(dob) : undefined,
      //New Fields
      homePhone,
      workPhone,
      address,
      city,
      state,
      country,
      zipCode,
      idNumber,
      employer,
      gender,
      emergencyName,
      emergencyPhone,
      relationship,
    };
    form.setValues(_data);
    form.resetDirty(_data);
    _data.dob && setisOldEnough(isAdult(_data.dob));
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState(data!);
    toggleFormActive();
  };

  const openModalClientGeneration = useCallback(() => {
    const { firstName, lastName } = data?.clientOne?.user || {};
    modals.openModal({
      closeOnClickOutside: false,
      title: "Generate Login",
      children: (
        <AutoLoginGeneration
          clientId={clientId || ""}
          firstName={firstName}
          lastName={lastName}
          fetchClient={fetchGuest}
        />
      ),
    });
  }, [clientId, data, fetchGuest]);

  const isFormUpdated = form.isDirty() && form.isTouched();
  return (
    <>
      {isAllowWrite && isOldEnough && !data?.clientOne?.user?.username && (
        <Button mb={"sm"} onClick={openModalClientGeneration}>
          Generate Login
        </Button>
      )}

      <FormCard
        title={"Basic Info"}
        loading={updateLoading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        isFormActive={isFormActive}
        hideCard={!clientId}
        overflow="visible"
      >
        <TextInput
          withAsterisk
          label="First Name"
          {...form.getInputProps("firstName")}
          disabled={isFormActive}
        />
        <TextInput
          withAsterisk
          label="Last Name"
          {...form.getInputProps("lastName")}
          disabled={isFormActive}
        />
        <TextInput
          withAsterisk
          label="Email"
          {...form.getInputProps("email")}
          disabled={!!clientId}
        />

        <DatePicker
          allowFreeInput
          dropdownPosition="bottom-start"
          label="Date of Birth"
          maxDate={new Date()}
          {...form.getInputProps("dob")}
          disabled={isFormActive}
        />

        <div>
          <Flex align="center" justify="space-between" gap={"xs"}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Home Number"
                {...form.getInputProps("homePhone")}
                disabled={isFormActive}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Work Number"
                {...form.getInputProps("workPhone")}
                disabled={isFormActive}
              />
            </Box>
          </Flex>

          <TextInput
            label="Address"
            {...form.getInputProps("address")}
            disabled={isFormActive}
          />

          <Flex align="center" justify="space-between" gap={"xs"}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="City"
                {...form.getInputProps("city")}
                disabled={isFormActive}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="State"
                {...form.getInputProps("state")}
                disabled={isFormActive}
              />
            </Box>
          </Flex>
          <Flex align="center" justify="space-between" gap={"xs"}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                width={"100%"}
                label="Country"
                {...form.getInputProps("country")}
                disabled={isFormActive}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                width={"100%"}
                label="Zip Code"
                {...form.getInputProps("zipCode")}
                disabled={isFormActive}
              />
            </Box>
          </Flex>

          <Flex align="center" justify="space-between" gap={"xs"}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="License/ID"
                {...form.getInputProps("idNumber")}
                disabled={isFormActive}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Select
                disabled={isFormActive}
                data={Object.values(Gender)}
                label="Gender"
                {...form.getInputProps("gender")}
              />
            </Box>
          </Flex>
          <TextInput
            label="Employer"
            {...form.getInputProps("employer")}
            disabled={isFormActive}
          />

          <ActionCard
            title="Emergency Contact"
            mt={"sm"}
            height={HeightCard.sm}
            radius={CardRadius.sm}
            size={"sm"}
            headerOnly
          >
            <TextInput
              label="Name"
              mt={0}
              {...form.getInputProps("emergencyName")}
              disabled={isFormActive}
            />
            <TextInput
              label="Phone"
              {...form.getInputProps("emergencyPhone")}
              disabled={isFormActive}
            />
            <TextInput
              label="Relationship"
              {...form.getInputProps("relationship")}
              disabled={isFormActive}
            />
          </ActionCard>
        </div>

        {!clientId && (
          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() => modals.closeAll()}
              style={{
                flex: 1,
                color: theme.colors.secondaryTextColor[6],
              }}
              sx={{
                backgroundColor: theme.colors.secondary[6],
                "&:hover": {
                  background: theme.colors.secondary[5],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              disabled={createLoading || updateLoading}
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={createLoading || updateLoading}
            >
              Create
            </Button>
          </Group>
        )}
      </FormCard>
    </>
  );
};

export default memo(GuestLeadProfile);
